import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { clearLocalStorage } from '@/utils/storageKeys';

// Handling the refresh call with common /api/v1/ version, else the route is throwing 404 and we are log outing the users
const apiVersion = '/api/v1';
const commonApiHttp = axios.create({
  baseURL: `${apiVersion}`,
  withCredentials: true,
});

commonApiHttp.interceptors.response.use((result) => result.data);
commonApiHttp.interceptors.response.use(null, (error) => {
  const { response: { status, data = {} } = {} } = error;
  if (status >= 400 && status < 500) {
    if (typeof data !== 'object') {
      return Promise.resolve(data);
    }
    if (!data?.status) data.status = status; // inject `status` if not already there
    return Promise.resolve(data);
  }
});

export async function fetchRefreshToken() {
  try {
    const result = await commonApiHttp.post('/auth/refresh');
    // If the token is not present try one more time - adding this as a fallback for extension and web app concurrently calling refresh and getting logged out
    if (!result?.data?.access_token) {
      await new Promise((resolve) => setTimeout(resolve, 2000)); // adding this as extension call might take some time to resolve
      const newResult = await commonApiHttp.post('/auth/refresh');
      return newResult;
    }
    return result;
  } catch {
    // intentionally silent
  }
}

async function logoutApp() {
  try {
    await commonApiHttp.delete('/auth/refresh');
  } catch {
    // intentionally silent
  }
}

export async function logout(skipRefreshTokenDelete = false) {
  try {
    if (!skipRefreshTokenDelete) {
      await logoutApp();
    }
    const lastVisitedPathname = window?.location?.pathname || '';
    const queryparam = window?.location?.search || '';
    clearLocalStorage();
    const targetPath = lastVisitedPathname
      ? lastVisitedPathname + queryparam
      : '/login';
    window.open(`${window?.location?.origin + targetPath}`, '_self');
  } catch {
    // intentionally silent
  }
}

let refreshSubscribers = [],
  isRefreshing = false,
  isLoggedOut = false;

// Add Broadcast Channel for token synchronization across tabs
const TOKEN_BROADCAST_CHANNEL = 'token_broadcast_channel';
const bc =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(TOKEN_BROADCAST_CHANNEL)
    : null;

// Listen for token updates from other tabs
if (bc) {
  bc.onmessage = (event) => {
    if (event.data && event.data.type === 'TOKEN_UPDATED' && event.data.token) {
      localStorage.setItem('apiv1Auth', event.data.token);
    }
  };
}

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshCompleted = (token) => {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
  // Broadcast the new token to all tabs
  if (bc && token && token.data && token.data.access_token) {
    bc.postMessage({
      type: 'TOKEN_UPDATED',
      token: token.data.access_token,
    });
  }
};

export const authInterceptor = (http, options = {}) => {
  const {
    getAccessToken,
    logout = () => {},
    shouldRefresh = () => {},
    shouldLogout = () => {},
    header = 'Authorization',
    headerPrefix = 'Bearer ',
    app = 'web',
  } = options;

  http.interceptors.request.use(
    function (config) {
      const token = getAccessToken();
      const contentType =
        config?.data instanceof FormData
          ? 'multipart/form-data'
          : 'application/json';
      config.headers = {
        [header]: `${headerPrefix}${token}`,
        'X-App': app,
        'X-Request-ID': uuidv4(),
        'Content-Type': contentType,
      };
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  commonApiHttp.interceptors.request.use(
    function (config) {
      const token = getAccessToken();
      const contentType =
        config?.data instanceof FormData
          ? 'multipart/form-data'
          : 'application/json';
      config.headers = {
        [header]: `${headerPrefix}${token}`,
        'X-App': app,
        'X-Request-ID': uuidv4(),
        'Content-Type': contentType,
      };
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  http.interceptors.response.use(null, function (error) {
    const { config, response: { status, data = {} } = {} } = error;
    const originalRequest = config;

    // Loggout if user is deleted or account suspended
    if (!isLoggedOut && shouldLogout(error)) {
      isLoggedOut = true;
      logout();
      return;
    }

    // Rotate refresh token, if token is expired.
    // Guard against an infinite loop: only refresh + retry a request ONCE. If a
    // request still fails the shouldRefresh check after a retry (e.g. an
    // endpoint that returns a persistent 401 the token refresh can't fix, like
    // agentforge's getAgents), fall through to the 4xx handling below instead
    // of refreshing + retrying forever.
    if (shouldRefresh(error) && !originalRequest?._retry) {
      if (originalRequest) originalRequest._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        fetchRefreshToken()
          .then((newToken) => {
            isRefreshing = false;
            onRefreshCompleted(newToken);
          })
          .catch(() => {
            // if token throws error, logout
            isLoggedOut = true;
            logout();
          });
      }
      const retryRequest = new Promise((resolve) => {
        subscribeTokenRefresh((token) => {
          localStorage.setItem('apiv1Auth', token.data.access_token);
          resolve(http(originalRequest));
        });
      });
      return retryRequest;
    }

    // 4xx responses from the server are expected to contain meaningful/actionable errors in the data structure
    // so return only the `data` part (just like for 2xx responses).
    // 5xx must reject so callers (e.g. mutations) surface failures instead of treating them as success.
    if (status >= 400 && status < 500) {
      // Check if fullResponse was requested
      if (config?.fullResponse) {
        return Promise.reject(error.response);
      }

      if (typeof data !== 'object') {
        return Promise.resolve(data);
      }
      if (!data?.status) data.status = status; // inject `status` if not already there
      return Promise.resolve(data);
    }

    return Promise.reject(error);
  });
};
